import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Share2, History, Archive, Plus, Trash2, Link2, Unlink, AlertTriangle, Info, RefreshCw, ChevronUp, ChevronDown,
  Copy, Printer, ExternalLink, Rocket, Handshake, X,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import {
  roadmapGet, roadmapSave, roadmapInsights, roadmapHistory, roadmapLink, roadmapUnlink, roadmapShare, roadmapUnshare,
  roadmapArchive, getSessions,
} from "@/lib/afmApi";
import { Loading, ErrorState } from "@/components/common";
import {
  PHASES, STATUS, KINDS, MILESTONE_TYPES, PLATFORMS, MonthsCalendar, Legend, AgendaList, StatusPill, ProgressBar,
  buildEvents, fmtDate, money, moneyMap, daysUntil, todayIso, PhasePipeline, PipelineLegend, Gantt, Agenda, overdueOf, MoneyBars,
} from "@/components/roadmap/shared";

const errText = (e) => {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : d?.message || "No se pudo completar";
};
const inputCls = "w-full rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-black outline-none focus:border-black disabled:bg-slate-50 disabled:text-slate-500";
const labelCls = "space-y-1 text-[10px] font-bold uppercase tracking-wider text-slate-500";
const uid = (p) => `${p}_${Math.random().toString(16).slice(2, 12)}`;
const CATS = { produccion: "Producción", mezcla: "Mezcla", master: "Máster", video: "Video", portada: "Portada / arte", marketing: "Marketing", prensa: "Prensa / PR", estudio: "Estudio", viajes: "Viajes", adelanto: "Adelanto al artista", otros: "Otros" };
const COST_COLS = ["produccion", "mezcla", "master", "video", "portada", "marketing"];
const ROLES = { compositor: "Compositor/a", letrista: "Letrista", productor: "Productor/a", interprete: "Intérprete", featuring: "Featuring", ingeniero: "Ingeniero/a", arreglista: "Arreglista", director: "Director/a", disenador: "Diseñador/a", otro: "Otro" };
const CURRENCIES = ["USD", "EUR", "VES", "COP", "MXN", "ARS", "CLP", "PEN", "DOP", "BRL", "GBP", "CAD"];
const newSong = (i) => ({ id: uid("s"), title: `Canción ${i + 1}`, release_date: "", notes: "", phases: Object.fromEntries(PHASES.map(([k]) => [k, { status: "pendiente", date: "" }])), mix: {}, video: {}, cover: {}, credits: [], marketing: [], budget: [], income: [], split_sheet_id: "", link: {} });

function Field({ label, children, className = "" }) {
  return <label className={`${labelCls} ${className}`}><span className="block">{label}</span>{children}</label>;
}

function Card({ title, right, children, tone }) {
  return (
    <div className={`rounded-xl border bg-white ${tone === "red" ? "border-[#CA0808]/40" : "border-slate-200"}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-4 py-2.5">
        <h4 className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#CA0808]">{title}</h4>
        {right && <div className="text-xs text-slate-500">{right}</div>}
      </div>
      <div className="space-y-3 p-4">{children}</div>
    </div>
  );
}

export default function HojaDeRutaDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [doc, setDoc] = useState(null);
  const [role, setRole] = useState("view");
  const [ins, setIns] = useState(null);
  const [failed, setFailed] = useState(false);
  const [save, setSave] = useState({ state: "ok", at: null });
  const [tab, setTab] = useState(0);
  const [view, setView] = useState("panorama");
  const [byMonth, setByMonth] = useState(false);
  const [sheets, setSheets] = useState([]);
  const [showShare, setShowShare] = useState(false);
  const [hist, setHist] = useState(null);
  const verRef = useRef(1);
  const docRef = useRef(null);
  const timer = useRef(null);
  const insTimer = useRef(null);
  const saving = useRef(false);
  const again = useRef(false);
  const ro = role !== "edit";

  const loadIns = useCallback(() => { roadmapInsights(id).then(setIns).catch(() => {}); }, [id]);
  const load = useCallback(() => {
    setFailed(false);
    roadmapGet(id).then((d) => { verRef.current = d.version; docRef.current = d; setDoc(d); setRole(d.role); }).catch(() => setFailed(true));
    loadIns();
  }, [id, loadIns]);
  useEffect(load, [load]);
  useEffect(() => { if (user?.id) getSessions(user.id).then((r) => setSheets(Array.isArray(r) ? r : [])).catch(() => {}); }, [user?.id]);
  useEffect(() => () => { clearTimeout(timer.current); clearTimeout(insTimer.current); }, []);

  const doSave = useCallback(async () => {
    if (saving.current) { again.current = true; return; }
    saving.current = true;
    setSave((s) => ({ ...s, state: "saving" }));
    try {
      const r = await roadmapSave(id, { ...docRef.current, version: verRef.current });
      verRef.current = r.version;
      setSave({ state: "ok", at: new Date() });
      clearTimeout(insTimer.current);
      insTimer.current = setTimeout(loadIns, 2500);
    } catch (e) {
      if (e?.response?.status === 409) {
        const cur = e.response.data?.detail?.current;
        toast.warning(errText(e));
        if (cur) { verRef.current = cur.version; docRef.current = cur; setDoc(cur); }
        setSave({ state: "ok", at: new Date() });
      } else {
        setSave((s) => ({ ...s, state: "error" }));
        toast.error(errText(e));
      }
    } finally {
      saving.current = false;
      if (again.current) { again.current = false; doSave(); }
    }
  }, [id, loadIns]);

  const change = (mut) => {
    if (ro) return;
    setDoc((d) => {
      const n = JSON.parse(JSON.stringify(d));
      mut(n);
      docRef.current = n;
      return n;
    });
    setSave((s) => ({ ...s, state: "dirty" }));
    clearTimeout(timer.current);
    timer.current = setTimeout(doSave, 1200);
  };

  const insBy = useMemo(() => Object.fromEntries((ins?.songs || []).map((s) => [s.id, s])), [ins]);
  const effective = useMemo(() => Object.fromEntries((ins?.songs || []).map((s) => [s.id, s.effective])), [ins]);
  const events = useMemo(() => (doc ? buildEvents(doc, effective) : []), [doc, effective]);

  if (failed) return <ErrorState onRetry={load} />;
  if (!doc) return <Loading />;
  const songs = doc.songs || [];
  const song = songs[Math.min(tab, songs.length - 1)];

  const doLink = async (s, code) => {
    try {
      const r = await roadmapLink(id, s.id, code);
      verRef.current = r.version;
      setDoc((d) => { const n = { ...d, songs: d.songs.map((x) => (x.id === s.id ? { ...x, link: r.link } : x)) }; docRef.current = n; return n; });
      toast.success(r.message);
      loadIns();
    } catch (e) { toast.error(errText(e)); }
  };
  const doUnlink = async (s) => {
    try {
      const r = await roadmapUnlink(id, s.id);
      verRef.current = r.version;
      setDoc((d) => { const n = { ...d, songs: d.songs.map((x) => (x.id === s.id ? { ...x, link: {} } : x)) }; docRef.current = n; return n; });
      loadIns();
    } catch (e) { toast.error(errText(e)); }
  };
  const archive = async () => {
    if (!window.confirm("¿Archivar esta hoja de ruta? Deja de verse en la lista.")) return;
    try { await roadmapArchive(id); toast.success("Archivada"); navigate("/hoja-de-ruta"); } catch (e) { toast.error(errText(e)); }
  };
  const openHistory = () => roadmapHistory(id).then((r) => setHist(r.history || [])).catch((e) => toast.error(errText(e)));

  const totalProgress = ins?.songs?.length ? Math.round(ins.songs.reduce((a, s) => a + s.progress, 0) / ins.songs.length) : 0;
  const nextRelease = songs.map((s) => s.release_date).filter((d) => d && d >= todayIso()).sort()[0];
  const rc = ins?.recoup;

  const overdue = overdueOf(doc, effective);
  const goSong = (sid) => { const i = songs.findIndex((x) => x.id === sid); if (i >= 0) setTab(i); setView("canciones"); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const cycle = (sid, k) => change((d) => {
    const x = d.songs.find((y) => y.id === sid);
    const order = ["pendiente", "en_curso", "hecho", "no_aplica"];
    x.phases[k].status = order[(order.indexOf(x.phases[k].status) + 1) % order.length];
    if (x.phases[k].status === "hecho" && !x.phases[k].date) x.phases[k].date = todayIso();
  });
  const moneyRows = songs.map((s) => {
    const inf = insBy[s.id] || {};
    const inc = role === "artist" ? (inf.income?.USD?.artist || 0) : (inf.income?.USD?.base || 0);
    return { id: s.id, title: s.title, cost: inf.cost_usd || 0, income: (inf.income_shared_with_other_song ? 0 : inc) + (inf.manual_income_usd || 0),
      note: inf.income_shared_with_other_song ? "Ingresos contados en otra canción del mismo lanzamiento" : inf.release ? "Ligada a Always" : (s.link?.isrc || s.link?.upc) ? "Fuera de Always · ingresos a mano" : "" };
  });
  const invested = moneyRows.reduce((a, r) => a + r.cost, 0);
  const generated = moneyRows.reduce((a, r) => a + r.income, 0);
  const VIEWS = [["panorama", "Panorama"], ["linea", "Línea de tiempo"], ["canciones", "Canciones"], ["dinero", "Dinero"]];

  return (
    <div className="space-y-6 pb-24" data-testid="roadmap-detail">
      <div className="space-y-4">
        <Link to="/hoja-de-ruta" className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-black"><ArrowLeft className="h-3.5 w-3.5" /> Hojas de ruta</Link>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#CA0808]">{doc.manager_name ? `${doc.manager_name} × ` : ""}{doc.artist_name || "Artista"}</div>
            {ro ? <h1 className="font-serif text-4xl font-bold tracking-tight text-black">{doc.title}</h1> : (
              <input value={doc.title} onChange={(e) => change((d) => { d.title = e.target.value; })} className="w-full bg-transparent font-serif text-4xl font-bold tracking-tight text-black outline-none" data-testid="rm-title" />
            )}
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-500">
              <select disabled={ro} value={doc.kind} onChange={(e) => change((d) => { d.kind = e.target.value; })} className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs">
                {Object.entries(KINDS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
              <span>{songs.length} canción{songs.length === 1 ? "" : "es"}</span>
              {role === "artist" && <span className="rounded-full bg-slate-100 px-2 py-0.5 font-semibold">Vista de artista</span>}
              {role === "view" && <span className="rounded-full bg-slate-100 px-2 py-0.5 font-semibold">Solo lectura</span>}
            </div>
            {(!ro || doc.description) && (
              <textarea disabled={ro} rows={2} value={doc.description || ""} onChange={(e) => change((d) => { d.description = e.target.value; })} placeholder="De qué va el proyecto, el orden de las canciones, la idea general…" className="mt-3 w-full max-w-3xl resize-y rounded-lg border border-transparent bg-transparent p-0 text-sm text-slate-600 outline-none hover:border-slate-200 focus:border-slate-300 focus:p-2" />
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <SaveBadge s={save} ro={ro} />
            {!ro && <button onClick={() => setShowShare(true)} className="flex items-center gap-1.5 rounded-lg bg-black px-3 py-2 text-sm font-semibold text-white" data-testid="rm-share"><Share2 className="h-4 w-4" /> Compartir / PDF</button>}
            <button onClick={openHistory} className="flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700"><History className="h-4 w-4" /> Historial</button>
            {!ro && <button onClick={archive} title="Archivar" className="rounded-lg border border-slate-300 p-2 text-slate-500 hover:text-red-600"><Archive className="h-4 w-4" /></button>}
          </div>
        </div>
        <div className="sticky top-0 z-20 -mx-1 flex gap-1 overflow-x-auto border-b border-slate-200 bg-[#FAFAFA] px-1">
          {VIEWS.map(([k, l]) => (
            <button key={k} onClick={() => setView(k)} data-testid={`rm-view-${k}`}
              className={`relative whitespace-nowrap px-4 py-3 text-sm font-semibold transition ${view === k ? "text-black" : "text-slate-500 hover:text-black"}`}>
              {l}{k === "panorama" && (overdue.length + (ins?.alerts || []).filter((a) => a.level === "alta").length) > 0 && <span className="ml-1.5 rounded-full bg-[#CA0808] px-1.5 text-[10px] font-bold text-white">{overdue.length + (ins?.alerts || []).filter((a) => a.level === "alta").length}</span>}
              {view === k && <span className="absolute inset-x-2 bottom-0 h-0.5 rounded-full bg-[#CA0808]" />}
            </button>
          ))}
        </div>
      </div>

      {view === "panorama" && (
        <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-3">
              <Kpi label="Avance" value={`${totalProgress}%`} sub="fases cerradas"><ProgressBar value={totalProgress} className="mt-2" /></Kpi>
              <Kpi label="Próxima salida" value={nextRelease ? fmtDate(nextRelease) : "—"} sub={nextRelease ? `${songs.find((s) => s.release_date === nextRelease)?.title} · en ${daysUntil(nextRelease)} días` : "pon las fechas de salida"} />
              <Kpi label="Invertido" value={moneyMap(ins?.cost_total)} sub={Object.keys(ins?.unpaid_total || {}).length ? `sin pagar: ${moneyMap(ins.unpaid_total)}` : "todo pagado"} />
              <Kpi label="Recoup" value={rc && !rc.hidden && rc.recoupable_usd ? `${rc.pct}%` : "—"} sub={rc && !rc.hidden && rc.recoupable_usd ? `faltan ${money(rc.pending_usd)} de ${money(rc.recoupable_usd)}` : "ningún gasto marcado"}>
                {rc && !rc.hidden && rc.recoupable_usd > 0 && <ProgressBar value={rc.pct} className="mt-2" />}
              </Kpi>
            </div>
            <div className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#CA0808]">Canciones</h3>
                <PipelineLegend />
              </div>
              {songs.map((s, i) => {
                const dd = daysUntil(s.release_date);
                return (
                  <div key={s.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border border-slate-200 bg-white px-4 py-3">
                    <span className="w-5 text-sm font-bold text-slate-400">{i + 1}</span>
                    <button onClick={() => goSong(s.id)} className="min-w-[140px] flex-1 text-left">
                      <div className="font-semibold text-black hover:underline">{s.title}</div>
                      <div className="text-xs text-slate-500">{s.release_date ? `${fmtDate(s.release_date, true)}${dd !== null ? (dd >= 0 ? ` · en ${dd} d` : " · ya salió") : ""}` : "Sin fecha de salida"}</div>
                    </button>
                    <PhasePipeline song={s} effective={effective[s.id]} onCycle={ro ? null : (k) => cycle(s.id, k)} />
                    <span className="w-10 text-right text-sm font-bold text-black">{insBy[s.id]?.progress ?? 0}%</span>
                  </div>
                );
              })}
              {!ro && <button onClick={() => { change((d) => { d.songs.push(newSong(d.songs.length)); }); }} className="flex items-center gap-1.5 rounded-lg border border-dashed border-slate-400 px-3 py-1.5 text-sm font-semibold text-slate-600"><Plus className="h-4 w-4" /> Añadir canción</button>}
            </div>
          </div>
          <aside className="space-y-4">
            <h3 className="text-[11px] font-bold uppercase tracking-[0.18em] text-black">Lo que toca ahora</h3>
            {ins?.alerts?.length > 0 && (
              <div className="space-y-1.5" data-testid="rm-alerts">
                {ins.alerts.slice(0, 6).map((a, i) => (
                  <button key={i} onClick={() => a.song_id && goSong(a.song_id)} className={`flex w-full items-start gap-2 rounded-lg border px-3 py-2 text-left text-sm ${a.level === "alta" ? "border-red-200 bg-red-50 text-red-800" : a.level === "media" ? "border-amber-200 bg-amber-50 text-amber-800" : "border-slate-200 bg-white text-slate-700"}`}>
                    {a.level === "info" ? <Info className="mt-0.5 h-4 w-4 shrink-0" /> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
                    <span>{a.text}</span>
                  </button>
                ))}
              </div>
            )}
            <Agenda events={events} overdue={overdue} />
          </aside>
        </div>
      )}

      {view === "linea" && (
        <div className="space-y-5">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <p className="max-w-2xl text-sm text-slate-500">Todo el proyecto en una pantalla: cada barra va de la primera entrega a la salida. Pasa el ratón por un punto para ver qué es; toca una canción para abrirla.</p>
            <div className="flex flex-wrap items-end gap-2">
              {!ro && <Field label="Desde"><input type="month" className={inputCls} value={doc.start_month || ""} onChange={(e) => change((d) => { d.start_month = e.target.value; })} /></Field>}
              {!ro && <Field label="Meses"><input type="number" min={1} max={24} className={`${inputCls} w-20`} value={doc.months || 6} onChange={(e) => change((d) => { d.months = Math.max(1, Math.min(24, Number(e.target.value) || 1)); })} /></Field>}
              <button onClick={() => setByMonth(!byMonth)} className="h-9 rounded-lg border border-slate-300 px-3 text-sm font-semibold text-slate-700">{byMonth ? "Ver línea de tiempo" : "Ver por meses"}</button>
            </div>
          </div>
          {byMonth ? <><Legend /><MonthsCalendar startMonth={doc.start_month} months={doc.months} events={events} /></> : <Gantt doc={doc} effective={effective} onSong={goSong} />}
          {!ro && <AddMilestone songs={songs} onAdd={(m) => change((d) => { d.milestones = [...(d.milestones || []), m].sort((a, b) => (a.date < b.date ? -1 : 1)); })} />}
          <div className="space-y-2">
            <h3 className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#CA0808]">Agenda completa</h3>
            <AgendaList events={events} songs={songs} readOnly={ro}
              onToggleMilestone={(mid) => change((d) => { const m = d.milestones.find((x) => x.id === mid); if (m) m.done = !m.done; })}
              onRemoveMilestone={(mid) => change((d) => { d.milestones = d.milestones.filter((x) => x.id !== mid); })} />
          </div>
        </div>
      )}

      {view === "canciones" && (
        <div className="grid gap-5 lg:grid-cols-[260px_1fr]">
          <div className="space-y-2 lg:sticky lg:top-16 lg:self-start">
            {songs.map((s, i) => (
              <button key={s.id} onClick={() => setTab(i)} className={`w-full rounded-xl border p-3 text-left transition ${song?.id === s.id ? "border-black bg-white shadow-sm" : "border-slate-200 bg-white/60 hover:border-slate-400"}`}>
                <div className="flex items-center justify-between gap-2"><span className="truncate text-sm font-semibold text-black">{i + 1}. {s.title}</span><span className="text-xs font-bold text-slate-500">{insBy[s.id]?.progress ?? 0}%</span></div>
                <div className="mt-0.5 text-[11px] text-slate-500">{s.release_date ? fmtDate(s.release_date, true) : "Sin fecha"}</div>
                <div className="mt-2"><PhasePipeline song={s} effective={effective[s.id]} compact /></div>
              </button>
            ))}
            {!ro && <button onClick={() => { change((d) => { d.songs.push(newSong(d.songs.length)); }); setTab(songs.length); }} className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-dashed border-slate-400 p-3 text-sm font-semibold text-slate-600"><Plus className="h-4 w-4" /> Añadir canción</button>}
          </div>
          {song && (
            <SongEditor key={song.id} s={song} i={songs.indexOf(song)} n={songs.length} info={insBy[song.id]} effective={effective[song.id]} ro={ro} role={role} doc={doc}
              sheets={sheets} change={change} onLink={doLink} onUnlink={doUnlink} onCycle={(k) => cycle(song.id, k)}
              onRemove={() => { if (window.confirm(`¿Eliminar «${song.title}» de la hoja de ruta?`)) { change((d) => { d.songs.splice(d.songs.findIndex((x) => x.id === song.id), 1); }); setTab(0); } }}
              onMove={(dir) => { const i = songs.indexOf(song); const j = i + dir; if (j < 0 || j >= songs.length) return; change((d) => { const [x] = d.songs.splice(i, 1); d.songs.splice(j, 0, x); }); setTab(j); }} />
          )}
        </div>
      )}

      {view === "dinero" && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Kpi label="Invertido" value={moneyMap(ins?.cost_total)} sub={invested ? `≈ ${money(invested)} en USD` : "sin gastos"} />
            <Kpi label="Generado" value={money(generated)} sub={role === "artist" ? "tu parte" : rc?.basis_label || "ingresos"} />
            <Kpi label="Sin pagar" value={moneyMap(ins?.unpaid_total)} sub="pagos pendientes" />
            <Kpi label="Resultado" value={generated - invested >= 0 ? `+${money(generated - invested)}` : `−${money(invested - generated)}`} sub={generated - invested >= 0 ? "el proyecto ya se pagó" : "lo que falta para pagarse"} />
          </div>
          <div className="space-y-2">
            <h3 className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#CA0808]">Invertido contra generado, por canción (USD)</h3>
            <MoneyBars rows={moneyRows} />
          </div>
          <RecoupPanel rc={rc} ins={ins} role={role} />
          <details className="rounded-xl border border-slate-200 bg-white">
            <summary className="cursor-pointer px-4 py-3 text-sm font-semibold text-black">Detalle por categoría y moneda</summary>
            <div className="overflow-x-auto border-t border-slate-200">
              <table className="w-full min-w-[900px] text-sm">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                  <tr><th className="px-3 py-2 text-left">Canción</th>{COST_COLS.map((c) => <th key={c} className="px-2 py-2 text-right">{CATS[c]}</th>)}<th className="px-2 py-2 text-right">Otros</th><th className="px-2 py-2 text-right">Total</th><th className="px-3 py-2 text-right">Sin pagar</th></tr>
                </thead>
                <tbody>
                  {songs.map((s) => {
                    const by = {}; const other = {};
                    for (const b of s.budget || []) {
                      if (b.amount == null || b.amount === "") continue;
                      const t = COST_COLS.includes(b.cat) ? (by[b.cat] = by[b.cat] || {}) : other;
                      t[b.currency] = (t[b.currency] || 0) + Number(b.amount);
                    }
                    return (
                      <tr key={s.id} className="border-t border-slate-100">
                        <td className="px-3 py-2 font-semibold text-black">{s.title}</td>
                        {COST_COLS.map((c) => <td key={c} className="px-2 py-2 text-right tabular-nums text-slate-700">{moneyMap(by[c])}</td>)}
                        <td className="px-2 py-2 text-right tabular-nums text-slate-700">{moneyMap(other)}</td>
                        <td className="px-2 py-2 text-right font-semibold tabular-nums text-black">{moneyMap(insBy[s.id]?.budget_total)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-amber-700">{moneyMap(insBy[s.id]?.budget_unpaid)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </details>
          <p className="text-xs text-slate-500">Los gastos se cargan en cada canción (pestaña Canciones › Presupuesto). Aquí solo se suman, para que no haya dos cifras del mismo gasto.</p>
        </div>
      )}

      {showShare && <ShareDialog doc={doc} onClose={() => setShowShare(false)} onChange={(share) => setDoc((d) => ({ ...d, share }))} />}
      {hist && <HistoryDrawer rows={hist} onClose={() => setHist(null)} />}
    </div>
  );
}

function SaveBadge({ s, ro }) {
  if (ro) return null;
  const t = s.at ? s.at.toLocaleTimeString("es-VE", { hour: "2-digit", minute: "2-digit" }) : "";
  const map = { ok: ["bg-emerald-500", t ? `Guardado ${t}` : "Al día"], dirty: ["bg-amber-400", "Cambios sin guardar…"], saving: ["bg-sky-500 animate-pulse", "Guardando…"], error: ["bg-red-500", "No se guardó: revisa tu conexión"] };
  const [dot, txt] = map[s.state] || map.ok;
  return <span className="flex items-center gap-1.5 text-xs text-slate-500" data-testid="rm-save-state"><span className={`h-2 w-2 rounded-full ${dot}`} />{txt}</span>;
}

function Kpi({ label, value, sub, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-1 truncate text-lg font-bold text-black" title={typeof value === "string" ? value : undefined}>{value}</div>
      {sub && <div className="truncate text-xs text-slate-500">{sub}</div>}
      {children}
    </div>
  );
}

function AddMilestone({ songs, onAdd }) {
  const [m, setM] = useState({ date: "", type: "hito", text: "", song_id: "" });
  const add = () => {
    if (!m.date || !m.text.trim()) { toast.error("Pon la fecha y qué es"); return; }
    onAdd({ ...m, id: uid("h"), text: m.text.trim(), done: false });
    setM({ date: "", type: "hito", text: "", song_id: "" });
  };
  return (
    <div className="grid gap-2 rounded-xl border border-slate-200 bg-white p-4 md:grid-cols-[150px_160px_1fr_180px_auto] md:items-end">
      <Field label="Fecha"><input type="date" className={inputCls} value={m.date} onChange={(e) => setM({ ...m, date: e.target.value })} /></Field>
      <Field label="Tipo"><select className={inputCls} value={m.type} onChange={(e) => setM({ ...m, type: e.target.value })}>{Object.entries(MILESTONE_TYPES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></Field>
      <Field label="Qué es"><input className={inputCls} value={m.text} onChange={(e) => setM({ ...m, text: e.target.value })} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Reunión mensual, entrevista, bolo, rodaje…" /></Field>
      <Field label="De qué canción (opcional)"><select className={inputCls} value={m.song_id} onChange={(e) => setM({ ...m, song_id: e.target.value })}><option value="">Del proyecto</option>{songs.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}</select></Field>
      <button onClick={add} className="h-9 rounded-lg bg-black px-4 text-sm font-semibold text-white">Añadir al calendario</button>
    </div>
  );
}

function RecoupPanel({ rc, ins, role }) {
  if (!rc || rc.hidden) return null;
  const inc = ins?.income_total || {};
  const incKey = role === "artist" ? "artist" : "base";
  return (
    <div className="grid gap-3 md:grid-cols-2">
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#CA0808]">Recoup</div>
        {rc.recoupable_usd ? (
          <>
            <div className="mt-2 flex items-baseline justify-between"><span className="text-sm text-slate-600">Recuperado</span><span className="text-xl font-bold text-black">{money(rc.recouped_usd)} <span className="text-sm font-normal text-slate-500">de {money(rc.recoupable_usd)}</span></span></div>
            <ProgressBar value={rc.pct} className="mt-2" />
            <p className="mt-3 text-xs text-slate-500">Se recupera con {rc.basis_label}{rc.artist_pct != null ? ` (${rc.artist_pct}% de la compañía)` : ""}. {rc.pending_usd > 0 ? `Faltan ${money(rc.pending_usd)}${rc.basis === "artist" ? ": el artista empieza a cobrar cuando se cubra." : "."}` : rc.basis === "artist" ? `Cubierto: al artista le tocan ${money(rc.payable_usd)}.` : "Cubierto."}</p>
          </>
        ) : <p className="mt-2 text-sm text-slate-500">Ningún gasto está marcado como «se recupera». Márcalo en el presupuesto de cada canción.</p>}
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#CA0808]">Ingresos</div>
        {Object.keys(inc).length ? (
          <div className="mt-2 space-y-1 text-sm">
            {Object.entries(inc).map(([cur, v]) => (
              <div key={cur} className="flex justify-between"><span className="text-slate-600">Regalías ({cur}){role === "artist" ? " · tu parte" : rc.basis === "income" ? "" : ` · ${rc.basis_label}`}</span><span className="font-semibold tabular-nums text-black">{money(v[incKey] ?? v.base, cur)}</span></div>
            ))}
            {role !== "artist" && Object.values(inc).some((v) => v.company != null) && (
              <div className="flex justify-between text-xs text-slate-500"><span>Parte de la compañía</span><span className="tabular-nums">{moneyMap(Object.fromEntries(Object.entries(inc).map(([c, v]) => [c, v.company])))}</span></div>
            )}
          </div>
        ) : <p className="mt-2 text-sm text-slate-500">{ins?.royalties_connected ? "Liga cada canción a su código (ISRC o UPC) para ver aquí sus regalías de Always. Si es música de otro lado, anota los ingresos a mano en la canción." : "Las regalías de Always no están conectadas en este servidor."}</p>}
        <p className="mt-2 text-[11px] text-slate-400">Regalías reales de la distribuidora, últimos 24 meses como mucho, más los ingresos anotados a mano.</p>
      </div>
    </div>
  );
}

function SongEditor({ s, i, n, info, effective = {}, ro, role, doc, sheets, change, onLink, onUnlink, onCycle, onRemove, onMove }) {
  const set = (mut) => change((d) => { const x = d.songs.find((y) => y.id === s.id); if (x) mut(x); });
  const [code, setCode] = useState(s.link?.isrc || s.link?.upc || "");
  const sp = info?.split_sheet;
  const authorSum = (s.credits || []).reduce((a, c) => a + (Number(c.author_pct) || 0), 0);
  const masterSum = (s.credits || []).reduce((a, c) => a + (Number(c.master_pct) || 0), 0);
  const byCat = {};
  for (const b of s.budget || []) (byCat[b.cat] = byCat[b.cat] || []).push(b);
  const usedCats = Object.keys(CATS).filter((c) => byCat[c]?.length || COST_COLS.includes(c));
  const rel = info?.release;
  const distTo = doc.owner_kind === "manager" && doc.artist_id ? `/distribute?para=${doc.artist_id}` : "/distribute";

  return (
    <div className="space-y-4" data-testid="rm-song">
      <Card title="La canción" right={`${info?.progress ?? 0}% de las fases cerradas`}>
        <div className="grid gap-3 md:grid-cols-[1fr_180px_auto]">
          <Field label="Título"><input disabled={ro} className={inputCls} value={s.title} onChange={(e) => set((x) => { x.title = e.target.value; })} /></Field>
          <Field label="Fecha de salida"><input type="date" disabled={ro} className={inputCls} value={s.release_date || ""} onChange={(e) => set((x) => { x.release_date = e.target.value; })} /></Field>
          {!ro && (
            <div className="flex items-end gap-1">
              <button onClick={() => onMove(-1)} disabled={i === 0} title="Subir en el orden" className="rounded-lg border border-slate-300 p-2 disabled:opacity-30"><ChevronUp className="h-4 w-4" /></button>
              <button onClick={() => onMove(1)} disabled={i === n - 1} title="Bajar en el orden" className="rounded-lg border border-slate-300 p-2 disabled:opacity-30"><ChevronDown className="h-4 w-4" /></button>
              <button onClick={onRemove} disabled={n === 1} title="Eliminar canción" className="rounded-lg border border-red-200 p-2 text-red-600 disabled:opacity-30"><Trash2 className="h-4 w-4" /></button>
            </div>
          )}
        </div>
        <Field label="Notas"><textarea disabled={ro} rows={3} className={inputCls} value={s.notes || ""} onChange={(e) => set((x) => { x.notes = e.target.value; })} placeholder="Capítulo de la historia, riesgos, entregas, a quién se le manda…" /></Field>
      </Card>

      <Card title="Fases" right="Toca un círculo para avanzar la fase · las que tienen candado se marcan solas">
        <PhasePipeline song={s} effective={effective} onCycle={ro ? null : onCycle} />
        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
          {PHASES.map(([k, label]) => {
            const a = effective[k];
            return (
              <div key={k} className="space-y-1 rounded-lg border border-slate-100 p-2">
                <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}{a?.auto && <span className="normal-case tracking-normal text-slate-400">{a.note}</span>}</div>
                {a?.auto ? <StatusPill status={a.status} auto note={a.note} /> : (
                  <select disabled={ro} value={s.phases[k].status} onChange={(e) => set((x) => { x.phases[k].status = e.target.value; })}
                    className={`w-full rounded-md border px-1.5 py-1 text-[11px] font-bold uppercase ${STATUS[s.phases[k].status]?.cls}`}>
                    {Object.entries(STATUS).map(([v, o]) => <option key={v} value={v}>{o.label}</option>)}
                  </select>
                )}
                <input type="date" disabled={ro} title="Fecha en la que toca o en la que se cerró" className="w-full rounded-md border border-slate-200 px-1.5 py-0.5 text-[11px] text-slate-600" value={s.phases[k].date || ""} onChange={(e) => set((x) => { x.phases[k].date = e.target.value; })} />
              </div>
            );
          })}
        </div>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Lanzamiento en Always" right={rel ? "Ligado" : s.link?.isrc || s.link?.upc ? "Código guardado (fuera de Always)" : "Sin ligar"}>
          {role !== "artist" && !ro && (
            <div className="flex gap-2">
              <input className={`${inputCls} font-mono uppercase`} value={code} onChange={(e) => setCode(e.target.value)} placeholder="ISRC o UPC del tema" />
              <button onClick={() => onLink(s, code)} className="flex shrink-0 items-center gap-1 rounded-lg bg-black px-3 text-sm font-semibold text-white"><Link2 className="h-4 w-4" /> Ligar</button>
              {(s.link?.isrc || s.link?.upc || s.link?.release_id) && <button onClick={() => { onUnlink(s); setCode(""); }} title="Quitar vínculo" className="rounded-lg border border-slate-300 px-2 text-slate-500"><Unlink className="h-4 w-4" /></button>}
            </div>
          )}
          {rel ? (
            <div className="space-y-2 text-sm">
              <div className="flex flex-wrap gap-2">
                {Object.entries(rel.store_links || {}).slice(0, 8).map(([k, u]) => <a key={k} href={u} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full border border-slate-300 px-2 py-0.5 text-xs font-semibold capitalize text-slate-700 hover:border-black">{k}<ExternalLink className="h-3 w-3" /></a>)}
              </div>
              <p className="text-xs text-slate-500">Distribución, máster y portada se marcan solos desde este lanzamiento{rel.upc ? ` (UPC ${rel.upc})` : ""}.</p>
              {info?.income && Object.keys(info.income).length > 0 && (
                <p className="text-xs text-slate-600">Regalías: <strong>{moneyMap(Object.fromEntries(Object.entries(info.income).map(([c, v]) => [c, role === "artist" ? v.artist : v.base])))}</strong>{info.income_shared_with_other_song ? " (del lanzamiento completo, ya contado en otra canción)" : ""}</p>
              )}
            </div>
          ) : (
            <div className="space-y-2 text-xs text-slate-500">
              <p>Si el tema está en Always, pon su ISRC o UPC: sus estados e ingresos salen solos. Si es de otra distribuidora, el código se guarda y los ingresos se anotan a mano (o no).</p>
              {!ro && role !== "artist" && <Link to={distTo} className="inline-flex items-center gap-1 font-semibold text-black underline"><Rocket className="h-3.5 w-3.5" /> Distribuir esta canción con Always</Link>}
            </div>
          )}
        </Card>

        <Card title="Legal · split sheet" right={sp ? (sp.status === "sealed" ? "Firmado por todos" : sp.status === "unknown" ? "" : `${sp.signed} de ${sp.total} firmas`) : ""}>
          {!ro && (
            <select className={inputCls} value={s.split_sheet_id || ""} onChange={(e) => set((x) => { x.split_sheet_id = e.target.value; })}>
              <option value="">Sin split sheet ligado</option>
              {sheets.map((sh) => <option key={sh.id} value={sh.id}>{sh.title || sh.song_title || sh.id}{sh.status === "sealed" ? " · firmado" : ""}</option>)}
              {s.split_sheet_id && !sheets.some((sh) => sh.id === s.split_sheet_id) && <option value={s.split_sheet_id}>{sp?.title || "Split sheet ligado"}</option>}
            </select>
          )}
          <p className="text-xs text-slate-500">Cuando el split sheet ligado quede firmado por todos, la fase Legal se marca sola. Los créditos de abajo te sirven para armarlo.</p>
          {!ro && <Link to="/split-sheets" className="inline-flex items-center gap-1 text-xs font-semibold text-black underline"><Handshake className="h-3.5 w-3.5" /> Crear un split sheet</Link>}
        </Card>
      </div>

      <Card title="Créditos y reparto" right={(s.credits || []).length ? `Autoría ${authorSum}% · Máster ${masterSum}%` : "Quién hizo qué"}>
        {(authorSum > 100 || masterSum > 100) && <p className="rounded-md bg-red-50 px-2 py-1 text-xs text-red-700">Los porcentajes pasan del 100%.</p>}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm">
            <thead className="text-[10px] uppercase tracking-wider text-slate-500"><tr><th className="py-1 text-left">Nombre</th><th className="text-left">Rol</th><th className="text-left">Correo</th><th className="text-left">% Autoría</th><th className="text-left">% Máster</th><th className="text-left">IPI</th><th className="text-left">Sociedad</th><th /></tr></thead>
            <tbody>
              {(s.credits || []).map((c, k) => (
                <tr key={c.id} className="border-t border-slate-100">
                  {[["name", "w-44"], ["role", "w-36"], ["email", "w-48"], ["author_pct", "w-20"], ["master_pct", "w-20"], ["ipi", "w-28"], ["pro", "w-28"]].map(([f, w]) => (
                    <td key={f} className={`py-1 pr-1 ${w}`}>
                      {f === "role" ? (
                        <select disabled={ro} className={inputCls} value={c.role} onChange={(e) => set((x) => { x.credits[k].role = e.target.value; })}>{Object.entries(ROLES).map(([r, l]) => <option key={r} value={r}>{l}</option>)}</select>
                      ) : (
                        <input disabled={ro} type={f.endsWith("pct") ? "number" : "text"} min={0} max={100} className={inputCls} value={c[f] ?? ""} onChange={(e) => set((x) => { x.credits[k][f] = e.target.value; })} placeholder={f === "pro" ? "SGAE, ASCAP…" : ""} />
                      )}
                    </td>
                  ))}
                  <td>{!ro && <button onClick={() => set((x) => { x.credits.splice(k, 1); })} className="p-1 text-red-500"><X className="h-4 w-4" /></button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!ro && <button onClick={() => set((x) => { x.credits = [...(x.credits || []), { id: uid("cr"), name: "", role: "compositor", email: "", author_pct: "", master_pct: "", ipi: "", pro: "" }]; })} className="flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700"><Plus className="h-3.5 w-3.5" /> Añadir persona</button>}
      </Card>

      <Card title="Calendario de marketing" right="Lo que se publica cada día, por plataforma">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm">
            <thead className="text-[10px] uppercase tracking-wider text-slate-500"><tr><th className="py-1 text-left">Fecha</th>{PLATFORMS.map(([k, l]) => <th key={k} className="text-left">{l}</th>)}<th /></tr></thead>
            <tbody>
              {(s.marketing || []).map((m, k) => (
                <tr key={m.id} className="border-t border-slate-100">
                  <td className="w-40 py-1 pr-1"><input type="date" disabled={ro} className={inputCls} value={m.date || ""} onChange={(e) => set((x) => { x.marketing[k].date = e.target.value; })} /></td>
                  {PLATFORMS.map(([p]) => <td key={p} className="py-1 pr-1"><input disabled={ro} className={inputCls} value={m[p] || ""} onChange={(e) => set((x) => { x.marketing[k][p] = e.target.value; })} placeholder="—" /></td>)}
                  <td>{!ro && <button onClick={() => set((x) => { x.marketing.splice(k, 1); })} className="p-1 text-red-500"><X className="h-4 w-4" /></button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!ro && (
          <div className="flex flex-wrap gap-2">
            <button onClick={() => set((x) => { x.marketing = [...(x.marketing || []), { id: uid("mk"), date: "", instagram: "", tiktok: "", youtube: "", offline: "" }]; })} className="flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700"><Plus className="h-3.5 w-3.5" /> Añadir día</button>
            {s.release_date && !(s.marketing || []).length && (
              <button onClick={() => set((x) => { x.marketing = plantillaMarketing(x.release_date); })} className="rounded-lg border border-dashed border-slate-400 px-3 py-1.5 text-xs font-semibold text-slate-600">Usar plantilla básica alrededor de la salida</button>
            )}
          </div>
        )}
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Visualizer / video">
          <Field label="Concepto"><textarea disabled={ro} rows={2} className={inputCls} value={s.video?.concept || ""} onChange={(e) => set((x) => { x.video = { ...(x.video || {}), concept: e.target.value }; })} /></Field>
          <Field label="Referentes"><textarea disabled={ro} rows={2} className={inputCls} value={s.video?.refs || ""} onChange={(e) => set((x) => { x.video = { ...(x.video || {}), refs: e.target.value }; })} placeholder="Enlaces o descripción" /></Field>
          <div className="grid gap-2 sm:grid-cols-2">
            <Field label="Fecha de entrega"><input type="date" disabled={ro} className={inputCls} value={s.video?.due || ""} onChange={(e) => set((x) => { x.video = { ...(x.video || {}), due: e.target.value }; })} /></Field>
            <Field label="Video final (enlace)"><input disabled={ro} className={inputCls} value={s.video?.url || ""} onChange={(e) => set((x) => { x.video = { ...(x.video || {}), url: e.target.value }; })} placeholder="https://" /></Field>
          </div>
        </Card>
        <Card title="Portada">
          <Field label="Concepto"><textarea disabled={ro} rows={2} className={inputCls} value={s.cover?.concept || ""} onChange={(e) => set((x) => { x.cover = { ...(x.cover || {}), concept: e.target.value }; })} /></Field>
          <Field label="Referentes"><textarea disabled={ro} rows={2} className={inputCls} value={s.cover?.refs || ""} onChange={(e) => set((x) => { x.cover = { ...(x.cover || {}), refs: e.target.value }; })} placeholder="Enlaces o descripción" /></Field>
          <div className="grid gap-2 sm:grid-cols-2">
            <Field label="Fecha de entrega"><input type="date" disabled={ro} className={inputCls} value={s.cover?.due || ""} onChange={(e) => set((x) => { x.cover = { ...(x.cover || {}), due: e.target.value }; })} /></Field>
            <Field label="Arte final (enlace)"><input disabled={ro} className={inputCls} value={s.cover?.url || ""} onChange={(e) => set((x) => { x.cover = { ...(x.cover || {}), url: e.target.value }; })} placeholder="https://" /></Field>
          </div>
        </Card>
      </div>

      <Card title="Mezcla y máster" right="Quién lo hace y dónde están los archivos">
        <div className="grid gap-2 md:grid-cols-2">
          <Field label="Ingeniero/a"><input disabled={ro} className={inputCls} value={s.mix?.engineer || ""} onChange={(e) => set((x) => { x.mix = { ...(x.mix || {}), engineer: e.target.value }; })} /></Field>
          <Field label="Carpeta de archivos"><input disabled={ro} className={inputCls} value={s.mix?.folder || ""} onChange={(e) => set((x) => { x.mix = { ...(x.mix || {}), folder: e.target.value }; })} placeholder="https://" /></Field>
        </div>
        <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-4">
          {[["vocals", "Voces"], ["stems", "Stems"], ["master", "Máster"], ["live", "Máster para directo"]].map(([k, l]) => (
            <Field key={k} label={l}><input disabled={ro} className={inputCls} value={s.mix?.[k] || ""} onChange={(e) => set((x) => { x.mix = { ...(x.mix || {}), [k]: e.target.value }; })} placeholder="Enlace o estado" /></Field>
          ))}
        </div>
      </Card>

      <Card title="Presupuesto" tone="red" right={`${moneyMap(info?.budget_total)} en total · ${moneyMap(info?.budget_unpaid)} sin pagar`}>
        <p className="text-xs text-slate-500">Cada gasto en su moneda. Si no es en USD, pon su equivalente en USD para sumarlo al recoup. Marca «Se recupera» si sale de las regalías antes de pagarle al artista.</p>
        {usedCats.map((cat) => {
          const rows = byCat[cat] || [];
          const sub = {};
          rows.forEach((b) => { if (b.amount != null) sub[b.currency] = (sub[b.currency] || 0) + Number(b.amount); });
          return (
            <div key={cat} className="rounded-lg border border-slate-100">
              <div className="flex items-center justify-between bg-slate-50 px-3 py-1.5">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">{CATS[cat]}</span>
                <span className="flex items-center gap-2 text-xs font-semibold text-slate-700">{moneyMap(sub)}
                  {!ro && <button onClick={() => set((x) => { x.budget = [...(x.budget || []), { id: uid("b"), cat, concept: "", vendor: "", amount: "", currency: doc.base_currency || "USD", usd: "", paid: false, recoupable: false, due: "" }]; })} className="rounded border border-slate-300 bg-white px-2 py-0.5 text-[10px] font-bold uppercase">+ Línea</button>}
                </span>
              </div>
              {rows.length > 0 && (
                <div className="hidden gap-1.5 px-3 pt-2 text-[9px] font-bold uppercase tracking-wider text-slate-400 md:grid md:grid-cols-[1fr_140px_110px_80px_110px_130px_120px_auto]">
                  <span>Concepto</span><span>Quién</span><span className="text-right">Monto</span><span>Moneda</span><span className="text-right">≈ USD</span><span>Fecha de pago</span><span>Estado</span><span />
                </div>
              )}
              {rows.length === 0 ? <p className="px-3 py-2 text-xs text-slate-400">Sin partidas.</p> : rows.map((b) => {
                const k = s.budget.findIndex((y) => y.id === b.id);
                const upd = (f, v) => set((x) => { x.budget[k][f] = v; });
                return (
                  <div key={b.id} className="grid gap-1.5 border-t border-slate-100 px-3 py-2 md:grid-cols-[1fr_140px_110px_80px_110px_130px_120px_auto] md:items-center">
                    <input disabled={ro} className={inputCls} value={b.concept || ""} onChange={(e) => upd("concept", e.target.value)} placeholder="Concepto" />
                    <input disabled={ro} className={inputCls} value={b.vendor || ""} onChange={(e) => upd("vendor", e.target.value)} placeholder="Quién (opcional)" />
                    <input disabled={ro} type="number" min={0} step="0.01" className={`${inputCls} text-right`} value={b.amount ?? ""} onChange={(e) => upd("amount", e.target.value)} placeholder="0,00" />
                    <input disabled={ro} list="rm-cur" className={`${inputCls} uppercase`} value={b.currency || ""} onChange={(e) => upd("currency", e.target.value.toUpperCase().slice(0, 3))} />
                    {b.currency && b.currency !== "USD" ? <input disabled={ro} type="number" min={0} step="0.01" className={`${inputCls} text-right ${b.recoupable && !b.usd ? "border-amber-400" : ""}`} value={b.usd ?? ""} onChange={(e) => upd("usd", e.target.value)} placeholder="≈ USD" title="Equivalente en USD" /> : <span className="hidden md:block" />}
                    <input disabled={ro} type="date" title="Fecha de pago" className={inputCls} value={b.due || ""} onChange={(e) => upd("due", e.target.value)} />
                    <select disabled={ro} value={b.paid ? "1" : "0"} onChange={(e) => upd("paid", e.target.value === "1")} className={`${inputCls} font-semibold ${b.paid ? "border-emerald-400 text-emerald-700" : "border-amber-400 text-amber-700"}`}><option value="1">Pagado</option><option value="0">No pagado</option></select>
                    <div className="flex items-center gap-2">
                      <label className="flex items-center gap-1 text-[11px] font-semibold text-slate-600"><input disabled={ro} type="checkbox" checked={!!b.recoupable} onChange={(e) => upd("recoupable", e.target.checked)} className="accent-[#CA0808]" /> Se recupera</label>
                      {!ro && <button onClick={() => set((x) => { x.budget.splice(k, 1); })} className="p-1 text-red-500"><X className="h-4 w-4" /></button>}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
        {!ro && (
          <select value="" onChange={(e) => e.target.value && set((x) => { x.budget = [...(x.budget || []), { id: uid("b"), cat: e.target.value, concept: "", vendor: "", amount: "", currency: doc.base_currency || "USD", usd: "", paid: false, recoupable: false, due: "" }]; })} className="rounded-lg border border-dashed border-slate-400 px-2 py-1.5 text-xs font-semibold text-slate-600">
            <option value="">+ Otra categoría…</option>
            {Object.entries(CATS).filter(([c]) => !usedCats.includes(c)).map(([c, l]) => <option key={c} value={c}>{l}</option>)}
          </select>
        )}
        <datalist id="rm-cur">{CURRENCIES.map((c) => <option key={c} value={c} />)}</datalist>
      </Card>

      <Card title="Ingresos anotados a mano" right="Para música de otras distribuidoras, shows, sync…">
        {(s.income || []).map((b, k) => (
          <div key={b.id} className="grid gap-1.5 md:grid-cols-[150px_1fr_120px_80px_120px_auto] md:items-center">
            <input disabled={ro} type="date" className={inputCls} value={b.date || ""} onChange={(e) => set((x) => { x.income[k].date = e.target.value; })} />
            <input disabled={ro} className={inputCls} value={b.concept || ""} onChange={(e) => set((x) => { x.income[k].concept = e.target.value; })} placeholder="De dónde vino" />
            <input disabled={ro} type="number" min={0} step="0.01" className={`${inputCls} text-right`} value={b.amount ?? ""} onChange={(e) => set((x) => { x.income[k].amount = e.target.value; })} />
            <input disabled={ro} list="rm-cur" className={`${inputCls} uppercase`} value={b.currency || ""} onChange={(e) => set((x) => { x.income[k].currency = e.target.value.toUpperCase().slice(0, 3); })} />
            {b.currency && b.currency !== "USD" ? <input disabled={ro} type="number" min={0} step="0.01" className={`${inputCls} text-right`} value={b.usd ?? ""} onChange={(e) => set((x) => { x.income[k].usd = e.target.value; })} placeholder="≈ USD" /> : <span className="hidden md:block" />}
            {!ro && <button onClick={() => set((x) => { x.income.splice(k, 1); })} className="p-1 text-red-500"><X className="h-4 w-4" /></button>}
          </div>
        ))}
        {!(s.income || []).length && <p className="text-xs text-slate-400">Nada anotado. No hace falta si el tema está ligado a Always.</p>}
        {!ro && <button onClick={() => set((x) => { x.income = [...(x.income || []), { id: uid("in"), date: todayIso(), concept: "", amount: "", currency: doc.base_currency || "USD", usd: "" }]; })} className="flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700"><Plus className="h-3.5 w-3.5" /> Anotar ingreso</button>}
      </Card>
    </div>
  );
}

function plantillaMarketing(rd) {
  const [y, m, d] = rd.split("-").map(Number);
  const at = (n) => { const t = new Date(Date.UTC(y, m - 1, d + n)); return t.toISOString().slice(0, 10); };
  return [
    { id: uid("mk"), date: at(-21), instagram: "Pre-save activo y enlazado en la bio", tiktok: "", youtube: "", offline: "" },
    { id: uid("mk"), date: at(-7), instagram: "Teaser", tiktok: "Teaser con el gancho", youtube: "Short", offline: "Pitch a prensa" },
    { id: uid("mk"), date: at(0), instagram: "Lanzamiento", tiktok: "Lanzamiento", youtube: "Lanzamiento / visualizer", offline: "Nota de prensa" },
    { id: uid("mk"), date: at(7), instagram: "Detrás de cámaras", tiktok: "Tendencia con el audio", youtube: "", offline: "Radios" },
  ];
}

function ShareDialog({ doc, onClose, onChange }) {
  const [share, setShare] = useState(doc.share?.active ? doc.share : null);
  const [opts, setOpts] = useState({ show_budget: !!doc.share?.show_budget, show_income: !!doc.share?.show_income, show_notes: doc.share?.show_notes !== false });
  const [busy, setBusy] = useState(false);
  const url = share?.token ? `${window.location.origin}/hoja/${share.token}` : "";
  const on = async () => {
    setBusy(true);
    try { const r = await roadmapShare(doc.id, opts); setShare(r.share); onChange(r.share); toast.success("Enlace listo"); } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const off = async () => {
    if (!window.confirm("¿Desactivar el enlace? Quien lo tenga ya no podrá abrirlo.")) return;
    try { await roadmapUnshare(doc.id); setShare(null); onChange({ active: false }); toast.success("Enlace desactivado"); } catch (e) { toast.error(errText(e)); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-lg space-y-4 rounded-2xl bg-white p-6" onClick={(e) => e.stopPropagation()} data-testid="rm-share-dialog">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-serif text-xl font-bold text-black">Compartir con el artista o el equipo</h3>
            <p className="mt-1 text-sm text-slate-500">Un enlace de solo lectura (no hace falta cuenta) y desde ahí se descarga en PDF. Nunca muestra correos, IPI ni el reparto del sello.</p>
          </div>
          <button onClick={onClose} className="p-1 text-slate-400"><X className="h-5 w-5" /></button>
        </div>
        <div className="space-y-2 rounded-xl border border-slate-200 p-3 text-sm">
          {[["show_notes", "Mostrar notas y descripción"], ["show_budget", "Mostrar presupuesto y recoup"], ["show_income", "Mostrar ingresos"]].map(([k, l]) => (
            <label key={k} className="flex items-center gap-2"><input type="checkbox" checked={opts[k]} onChange={(e) => setOpts({ ...opts, [k]: e.target.checked })} className="accent-black" /> {l}</label>
          ))}
        </div>
        {share?.token ? (
          <div className="space-y-3">
            <div className="flex gap-2">
              <input readOnly value={url} className={`${inputCls} font-mono text-xs`} onFocus={(e) => e.target.select()} />
              <button onClick={() => { navigator.clipboard?.writeText(url); toast.success("Enlace copiado"); }} className="flex shrink-0 items-center gap-1 rounded-lg bg-black px-3 text-sm font-semibold text-white"><Copy className="h-4 w-4" /> Copiar</button>
            </div>
            <div className="flex flex-wrap gap-2">
              <a href={`https://wa.me/?text=${encodeURIComponent(`Hoja de ruta de «${doc.title}»: ${url}`)}`} target="_blank" rel="noreferrer" className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-700">Enviar por WhatsApp</a>
              <a href={`${url}?pdf=1`} target="_blank" rel="noreferrer" className="flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-700"><Printer className="h-4 w-4" /> Descargar PDF</a>
              <button onClick={on} disabled={busy} className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-700">Guardar opciones</button>
              <button onClick={off} className="rounded-lg border border-red-200 px-3 py-1.5 text-sm font-semibold text-red-600">Desactivar enlace</button>
            </div>
          </div>
        ) : (
          <button onClick={on} disabled={busy} className="w-full rounded-lg bg-black py-2.5 text-sm font-semibold text-white disabled:opacity-50">{busy ? "Creando…" : "Crear enlace"}</button>
        )}
      </div>
    </div>
  );
}

function HistoryDrawer({ rows, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={onClose}>
      <div className="h-full w-full max-w-md overflow-y-auto bg-white p-5" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between"><h3 className="font-serif text-xl font-bold text-black">Historial</h3><button onClick={onClose}><X className="h-5 w-5 text-slate-400" /></button></div>
        {rows.length === 0 ? <p className="text-sm text-slate-500">Sin cambios todavía.</p> : rows.map((r, i) => (
          <div key={i} className="border-b border-slate-100 py-2 text-sm">
            <div className="text-black">{r.what}</div>
            <div className="text-xs text-slate-500">{r.by_name || r.by} · {new Date(r.at).toLocaleString("es-VE")}</div>
          </div>
        ))}
        <button onClick={onClose} className="mt-4 flex items-center gap-1 text-xs text-slate-500"><RefreshCw className="h-3 w-3" /> Cerrar</button>
      </div>
    </div>
  );
}
